/**
 * CaseNavigator workspace-tree data hook — fetches the active workspace's
 * real on-disk folder/file tree via the `case.workspace_tree` gateway RPC
 * (hidden/internal entries filtered server-side).
 *
 * CCM Wave 2 (org-pane handoff `ccm-lexide-org-pane-handoff-2026-06-28.md`):
 * a thin ORG-SCOPE BRANCH on top of the case path. When the effective
 * scope is global (`useEffectiveScope() === "org"`, i.e. the active-context
 * selection is null — the per-user "Global" workspace), this fetches the
 * org tree via `caseWorkspaceTreeOrg` (`{scope:"org"}`, R3 `9ed2d9e7`);
 * otherwise the existing case path (selection ids). The org workspace is
 * byte-for-byte case-shaped, so the same `WorkspaceTreeFolder` shape feeds
 * `CaseFileNav`/`CaseFileTree` unchanged.
 *
 * The React-Query key is SCOPE-DISTINCT (`"org"` vs the caseId) so a
 * case ↔ org switch never cross-pollutes the cache.
 *
 * Disabled until the gateway is connected and the scope's required ids
 * are present (org needs none beyond connection; case needs the full
 * client/matter/case chain).
 *
 * ## Refresh on ingest (2026-08-07)
 *
 * An upload lands files under `sources/` and the tree used to keep showing the
 * pre-upload state until something else happened to refetch it — the lawyer
 * uploads a document, the receipt says it was added to the acervo, and the
 * navigator disagrees. Operator-reported; reproduced during the R8 run, where
 * every observation of new content required a manual reload.
 *
 * ⚠ **"There is no server event to subscribe to" was TRUE and is now FALSE**
 * (D-83, 2026-08-09). Kept rather than deleted, because it is the only thing
 * that explains why a client-side inference was ever the right call — without
 * it, the removed counter reads as laziness in `git log`. What WAS true when
 * this shipped: `case.updated` fires
 * only for created / deleted / status_changed / renamed, the single
 * `document.updated` emission is a migration broadcast on `document.read`, and
 * ingest completion broadcast nothing but the `chat` receipt — a STRING, which
 * this SPA does not parse by policy. So the only available signal was the one
 * the client already held: the ingest MISSION reaching a terminal state,
 * counted off the store the agents pane polls.
 *
 * 🔴 **That inference FAILED LIVE, measured** — see the block on the event
 * subscription below for the numbers. The operator ruled the runtime must emit
 * a real event and this surface must SUBSCRIBE.
 *
 * ## The counter is GONE (2026-08-09), and the precondition it waited on
 *
 * `plugin.ingest.state` is now the ONLY trigger. The counter was kept for one
 * reason — a runtime predating the emitter sends nothing, and removing the
 * fallback first would have left those runtimes with no trigger at all — so its
 * retirement was gated on the whole fleet carrying the emitter. That gate is
 * now MET, verified per-container rather than inferred from a tag:
 *
 *   - prod  — 5/5 on `lexruntime:2026.8.9-163d13d`, which contains the emitter
 *             commit `b0345dee7` (ancestry checked, not assumed)
 *   - local — 9/9 reporting `"commit":"b0345dee7"` from their own
 *             `GET /api/lx/version`
 *
 * Attribution was then re-measured with the counter ALREADY REMOVED, so the
 * observed refresh is the event path alone and not the two paths racing — the
 * differential reading the earlier item-6 run could not rule out.
 *
 * 🚨 **ROLLBACK FLOOR — this surface now has a minimum runtime version.**
 * With no fallback, a runtime that does not emit `plugin.ingest.state` leaves
 * the Acervo stale after an upload until the lawyer reloads — the original
 * operator-reported bug, returning SILENTLY (no error, no warning; a tree that
 * simply does not change). The emitter is `b0345dee7`, so:
 *
 *   image `2026.8.9-163d13d` or newer .... OK
 *   image `2026.8.8-aad85ad` or older .... STALE TREE until reload
 *
 * Verified 2026-08-09: ACR `:latest` → `2026.8.9-871e990` (emitter present) and
 * all 5 prod runtimes on `871e990`/`163d13d`, so the floor is satisfied today.
 * **Do not roll the runtime fleet back below that floor while this file has no
 * fallback** — revert this removal in the same change if you must.
 */

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  useActiveContextStore,
  useEffectiveScope,
} from "@/stores/useActiveContextStore";
import {
  scopeKeySegment,
  workspaceQueryEnabled,
} from "@/lib/workspace-scope";
import { useLexEventStream } from "@/hooks/useEventStream";
import type { QueryClient } from "@tanstack/react-query";
import type { CaseWorkspaceTreeResult } from "@/types/gateway";

/**
 * ⚖ Scope-match predicate shared by BOTH refresh events (`ingest.state` +
 * `workspace.changed`). Reads ONLY `caseId` — deliberately: the SPA refetches
 * and never patches, so every other payload field is informational and the
 * emitter can widen freely without a lockstep (workspace-refresh miniplan §2).
 * Exported for the W1-W3 pins.
 */
export function shouldRefreshForEvent(payload: unknown, openCaseId: string | undefined): boolean {
  const p = payload as { caseId?: unknown } | null;
  // Another case's event must not refetch the tree the lawyer is looking at;
  // a malformed payload (no string caseId) is ignored silently — malformed is
  // neither a crash nor a refetch-everything.
  return Boolean(
    p && typeof p.caseId === "string" && openCaseId && p.caseId === openCaseId,
  );
}

/**
 * The ONE invalidation set for "this case's workspace changed on disk" —
 * tree + folder-map + source-catalog (each line's rationale in the D-83
 * subscriber block below, where this body lived before `workspace.changed`
 * became its second caller). Exported for the refresh unit pins; the manual
 * refresh icons call it too, so auto and manual refresh cannot drift.
 */
export function invalidateWorkspaceCaches(queryClient: QueryClient, scopeKey: string): void {
  void queryClient.invalidateQueries({ queryKey: ["case-workspace-tree", scopeKey] });
  void queryClient.invalidateQueries({ queryKey: ["case-folder-map", scopeKey] });
  void queryClient.invalidateQueries({ queryKey: ["case-source-catalog", scopeKey] });
}

export function useCaseWorkspaceTree() {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selection = useActiveContextStore((s) => s.selection);
  const scope = useEffectiveScope();

  const clientId = selection?.clientId ?? undefined;
  const matterId = selection?.matterId ?? undefined;
  const caseId = selection?.caseId ?? undefined;
  const isOrg = scope === "org";

  const queryClient = useQueryClient();
  const scopeKey = scopeKeySegment(scope, caseId);

  // ── Refresh on ingest: the runtime's own ingest event (D-83) ──────────────
  //
  // ⚠ Why this is an EVENT and not an inference — the numbers that settled it.
  // The counter this replaced FAILED LIVE, measured
  // 2026-08-08 on `case-msj7cgh1-ppfvd3` with a stability-gated baseline:
  // `ACERVO 13` → upload → the chat receipt says *"processado — adicionado ao
  // acervo do caso"* → 300 s of polling with NO navigation, still 13 → a
  // RELOAD shows 14. The data was there the whole time; only the surface was
  // stale. So the lawyer was told a document had been filed by a pane that
  // then failed to show it. (`run-item6.mjs` reproduces it.)
  //
  // The operator ruled (D-83) that the runtime emits a real event and this
  // surface SUBSCRIBES rather than re-deriving completion client-side. Contract
  // agreed with `lexruntime-changes` on bus thread
  // `20260809T015012Z-…-5aih`; emitter rides the D-83 batch roll.
  //
  //   { event: "ingest.state"  (wire: "plugin.ingest.state"), caseId, missionId,
  //     status: "completed" | "failed" | "canceled" | "awaiting_user",
  //     terminal: boolean,                       // false ONLY for awaiting_user
  //     sources: { ok, ids?, total?, failed? },  // ⚠ total/failed OPTIONAL
  //     touchedRoots?: string[],                 // SEED root KEYS
  //     at }
  //
  // ⚠ `sources` is ABSENT on this rev (mission nodes carry no source
  // attribution yet). Derive nothing from that absence — refetch, as agreed.
  //
  // ⚠ **`total` and `failed` are OPTIONAL even when `sources` is PRESENT**
  // (`mission-ingest` handshake `20260812T210027Z`, accepted).
  //
  // AS BUILT (their follow-up, wider than the accepted scope): `ok`, `failed`
  // and `ids` all emit honestly — a failed intake node means the lawyer's file
  // is on disk as an UNUSABLE stub, which is worth a number. **`total` NEVER
  // emits**, and their build pins its absence with a test. A REFUSED archive
  // member never becomes a node, a source or a wake entry, so it is
  // structurally unreachable at emit time. Absence still means NOT ATTRIBUTED
  // — it does NOT mean zero, and the three-state read below still governs
  // because the field remains optional in the contract.
  //
  // 🚨 **NEVER RECONSTRUCT `total` AS `ok + failed`.** That sum counts only what
  // was PLANNED, so it is blind to refusals — it would answer "10 of 10" for an
  // upload where two members were refused before planning, which is the same
  // reassuring-direction lie as the missing count, now wearing arithmetic.
  // Refusals reach the lawyer through the `📦` receipt line, not this event.
  //
  // Two shape details for whoever consumes it: `ok` counts SOURCE RECORDS, not
  // completed nodes (a node that finishes without a `source_id` is not a
  // source); and case-wide stages are excluded by id PREFIX, not an
  // enumeration, so a new intake modality is covered without an edit.
  //
  // 🚨 THE FIRST CONSUMER OF THESE COUNTS OWES A THREE-STATE READ:
  //     failed === undefined  ⇒ NOT ATTRIBUTED — say nothing
  //     failed === 0          ⇒ attributed, and none failed
  //     failed > 0            ⇒ real failures
  // Never `failed > 0` alone: `undefined > 0` is FALSE, so a partial payload
  // would render as "all fine" — wrong in the reassuring direction, which is
  // the exact failure this event exists to end.
  //
  // There is NO consumer today: this hook uses the event purely as a refetch
  // TRIGGER and reads no field of `sources`. The Acervo's FALHOU badge is NOT
  // downstream of this — it reads the per-file `file.ingestStatus`
  // (`CaseFileRow.tsx`). Recorded because the handshake was scoped on the
  // reasonable belief that it was.
  //
  // ⚠ Refetch on EVERY status, terminal or not — including `failed` and
  // `canceled`. A partially-ingested upload still wrote files, and a park can
  // too, so refreshing only on success leaves the tree stale in exactly the
  // case the lawyer most needs to look at. `terminal` is NOT used to gate the
  // refresh; it exists so a future state we have never seen degrades to "keep
  // waiting" instead of being mishandled.
  //
  // ⚠ There is NO counter-based fallback any more, and its absence is
  // deliberate — see the header docblock for what was removed and why the
  // precondition is now met. Do not reintroduce a client-side inference here:
  // the whole point of D-83 is that completion is the runtime's fact to state,
  // not the client's to guess.
  // 🚨 The bare name is `ingest.state`, NOT `lx.ingest.state`. The agreed
  // contract wrote the event as `lx.ingest.state`, but the SHIPPED emitter is
  // `broadcast(lexEventName("ingest.state"), event)` — read out of the deployed
  // bundle on rev `dev-20260809.0225-b0345dee7`, not off the agreement. Since
  // `lexEventName` prepends `plugin.`, the wire name is `plugin.ingest.state`
  // and a subscriber on `lx.ingest.state` listens to `plugin.lx.ingest.state`:
  // a name nothing emits. It would have been SILENTLY INERT — no error, no
  // warning, just a tree that never refreshes, which is indistinguishable from
  // the bug this replaces. Verify the wire name against the artifact, never
  // against the contract text.
  useLexEventStream(
    "ingest.state",
    useCallback(
      (payload: unknown) => {
        // Scope-match on caseId: another case finishing an ingest must not
        // refetch the tree the lawyer is looking at.
        if (!shouldRefreshForEvent(payload, caseId) || !scopeKey) return;
        // The three-cache set lives in `invalidateWorkspaceCaches` (shared with
        // the `workspace.changed` subscription below AND the manual refresh
        // icons, so the surfaces cannot drift). Why each member is in the set:
        // - tree: the payload this hook serves.
        // - folder-map: ingest can mint a subzone (`sources/whatsapp/`), whose
        //   label + order come from the map.
        // - source-catalog (⚖ S5.0, 2026-08-11): its absence was a specific lie
        //   — the Acervo COUNT is tree-driven and updated live while the
        //   `categorias` GROUPING is catalog-driven and did not, so an
        //   un-reloaded pane showed a fresh total (176) beside a stale
        //   "everything uncategorized" (measured 2026-08-10). Load-bearing with
        //   compound-upload nesting: `node_id`/`parent_id` ride the CATALOG, so
        //   without this members render FLAT until a reload silently collapses
        //   175 rows into one node.
        // Refetch on EVERY status: a partial or failed ingest still wrote rows.
        invalidateWorkspaceCaches(queryClient, scopeKey);
      },
      [caseId, queryClient, scopeKey],
    ),
  );

  // ── Refresh on ANY workspace write: `workspace.changed` (operator directive
  // 2026-08-15, `lexide-workspace-refresh-miniplan.md`) ──────────────────────
  //
  // The D-83 event above covers INGEST missions only; a concierge `lx_write`
  // into `analyses/` emitted nothing, and during the A-14 live review the
  // operator had to FULL-RELOAD to see a document the runtime had just written.
  // This is the sibling subscription: same seam, same scope-match, same
  // invalidation set.
  //
  // ⚖ Contract (named by this lane, carried to the runtime verbatim):
  //   { caseId REQUIRED, op?, paths?, at? } — the SPA reads ONLY `caseId` and
  //   REFETCHES; it never patches from `paths`. Everything but `caseId` is
  //   informational, so the emitter can widen freely without lockstep.
  //
  // ⚠ Wire name: the runtime emits via `lexEventName("workspace.changed")`,
  // which prepends `plugin.` — the same prefix that silently killed the first
  // ingest subscriber. `useLexEventStream` owns that mapping; do not "fix" the
  // bare name here without re-reading that lesson (D-83 block above).
  //
  // Overlap with `ingest.state` is HARMLESS BY DESIGN: invalidation is
  // idempotent and react-query dedupes in-flight refetches — which is also the
  // burst answer (N rapid writes cost at most one refetch per settle). Until
  // the runtime emitter rolls, this subscription is INERT — a listener on a
  // name nothing emits — which is safe in exactly the way the A-11 renderer
  // was while its fields were absent.
  useLexEventStream(
    "workspace.changed",
    useCallback(
      (payload: unknown) => {
        if (!shouldRefreshForEvent(payload, caseId) || !scopeKey) return;
        invalidateWorkspaceCaches(queryClient, scopeKey);
      },
      [caseId, queryClient, scopeKey],
    ),
  );

  return useQuery<CaseWorkspaceTreeResult, Error>({
    // Scope-distinct key: org uses the literal "org", case uses the
    // caseId. A case ↔ org switch flips the key so neither tree reads
    // the other's cached payload.
    queryKey: ["case-workspace-tree", scopeKeySegment(scope, caseId)],
    queryFn: () =>
      isOrg
        ? rpc!.caseWorkspaceTreeOrg()
        : rpc!.caseWorkspaceTree({ clientId, matterId, caseId: caseId! }),
    enabled:
      Boolean(rpc) &&
      connectionState === "connected" &&
      workspaceQueryEnabled(scope, { clientId, matterId, caseId }),
    staleTime: 30_000,
    retry: 1,
  });
}
